import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./BpsLog.scss";

import logo from "../../assets/imgs/sysb_logo.svg";
import logpageIcon from "../../assets/imgs/brm/logpage-icon.svg";
import settingsIcon from "../../assets/imgs/brm/setting-icon.svg";

import SettingsModal from "./components/SettingsModal";
import BpsLogMap from "./components/BpsLogMap";

import { useBpsLogData } from "./hooks/useBpsLogData";
import { useBpsLogMetadata } from "./hooks/useBpsLogMetadata";

import {
  BPSLOG_SETTINGS_STORAGE_KEY,
  loadPersistedSettings,
  type MapActualBitrateColoring,
  type PersistedSettings,
} from "./utils/settings";
import { persistSettingsMerged } from "./utils/persist";
import type { MapTileLayer } from "./utils/types";
import {
  formatTimeLabel,
  parseDateTimeLocal,
  toDateTimeLocalValue,
} from "./utils/datetime";
import type { BpsLogMapPickInfo } from "./components/BpsLogMap";

import {
  DEFAULT_DELAY_OFFSET_MS,
  DEFAULT_FOLLOW_MAP,
  DEFAULT_MAP_TILE_LAYER,
  DEFAULT_SHOW_VIDEO,
  MAX_POINTS_FOR_UI,
  REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
} from "./constants";
const DEFAULT_DISABLE_CHART_DOWNSAMPLE = false;
const DEFAULT_DISABLE_MAP_DOWNSAMPLE = true;
const DEFAULT_MAP_ACTUAL_THRESHOLD_LOW_KBPS = 1300;
const DEFAULT_MAP_ACTUAL_THRESHOLD_MID_KBPS = 1700;

const DEFAULT_MAP_ACTUAL_BITRATE_COLORING: MapActualBitrateColoring = {
  thresholdLowKbps: DEFAULT_MAP_ACTUAL_THRESHOLD_LOW_KBPS,
  thresholdMidKbps: DEFAULT_MAP_ACTUAL_THRESHOLD_MID_KBPS,
  lowColor: "#ff4d47",
  midColor: "#ffec3a",
  highColor: "#00b900",
};

const DEFAULT_HEATMAP_CELL_METERS = 10;
const DEFAULT_HEATMAP_BASE_OPACITY = 0.08;
const DEFAULT_HEATMAP_RENDER_MODE: "grid" | "smooth" = "smooth";
const DEFAULT_HEATMAP_SCORE_COLOR_MID = 0.5;
const DEFAULT_HEATMAP_SCORE_COLOR_LOW = 0.9;

const BpsLogMapPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const persisted = useMemo(() => {
    return loadPersistedSettings({
      storageKey: BPSLOG_SETTINGS_STORAGE_KEY,
      defaultDelayOffsetMs: DEFAULT_DELAY_OFFSET_MS,
      // Mapページではチャート設定を扱わないため、デフォルトは空配列でよい。
      defaultCharts: [],
      defaultFollowMap: DEFAULT_FOLLOW_MAP,
      defaultShowVideo: DEFAULT_SHOW_VIDEO,
      defaultMapTileLayer: DEFAULT_MAP_TILE_LAYER,
      defaultMapActualBitrateColoring: DEFAULT_MAP_ACTUAL_BITRATE_COLORING,
    });
  }, []);

  const {
    baseName,
    basePoint,
    coursePoints,
    vehicleId,
    setVehicleId,
    vehicleOptions,
  } = useBpsLogMetadata({ navigate });

  const isRealtime = false;
  const realtimeWindowMinutes = 10;
  const realtimeRightEdgeFallbackLagMs = REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS;
  const maxPointsForUi = MAX_POINTS_FOR_UI;

  const defaultEnd = new Date();
  const defaultStart = new Date(defaultEnd.getTime() - 10 * 60 * 1000);
  const initialStartInput = toDateTimeLocalValue(defaultStart);
  const initialEndInput = toDateTimeLocalValue(defaultEnd);

  const [startInput, setStartInput] = useState(initialStartInput);
  const [endInput, setEndInput] = useState(initialEndInput);
  const [appliedStartInput, setAppliedStartInput] = useState(initialStartInput);
  const [appliedEndInput, setAppliedEndInput] = useState(initialEndInput);

  const [appliedFetchNonce, setAppliedFetchNonce] = useState(0);

  const [isPeriodRenderBusy, setIsPeriodRenderBusy] = useState(false);
  const [renderCancelToken, setRenderCancelToken] = useState(0);

  const consumedNavStateRef = useRef(false);
  useEffect(() => {
    if (consumedNavStateRef.current) return;
    const raw = (location as any)?.state as any;
    if (typeof raw !== "object" || raw === null) return;

    const start = typeof raw.startInput === "string" ? raw.startInput : null;
    const end = typeof raw.endInput === "string" ? raw.endInput : null;
    if (!start || !end) return;

    const sAt = parseDateTimeLocal(start);
    const eAt = parseDateTimeLocal(end);
    if (!sAt || !eAt) return;

    consumedNavStateRef.current = true;
    setStartInput(start);
    setEndInput(end);
    setAppliedStartInput(start);
    setAppliedEndInput(end);
  }, [location]);

  const pendingNavVehicleIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (pendingNavVehicleIdRef.current !== null) return;
    const raw = (location as any)?.state as any;
    const v = raw?.vehicleId;
    if (typeof v === "number" && Number.isFinite(v)) {
      pendingNavVehicleIdRef.current = v;
    }
  }, [location]);

  useEffect(() => {
    const target = pendingNavVehicleIdRef.current;
    if (target === null) return;
    if (vehicleId === target) {
      pendingNavVehicleIdRef.current = null;
      return;
    }
    const exists = vehicleOptions.some((o) => o?.value === target);
    if (!exists) return;
    setVehicleId(target);
    pendingNavVehicleIdRef.current = null;
  }, [vehicleId, vehicleOptions, setVehicleId]);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const [delayOffsetMs, setDelayOffsetMs] = useState<number>(() => {
    return typeof persisted.delayOffsetMs === "number" &&
      Number.isFinite(persisted.delayOffsetMs)
      ? persisted.delayOffsetMs
      : DEFAULT_DELAY_OFFSET_MS;
  });

  const [followMap, setFollowMap] = useState<boolean>(() => {
    return typeof persisted.followMap === "boolean"
      ? persisted.followMap
      : DEFAULT_FOLLOW_MAP;
  });

  // SettingsModalのprops形状との互換性のために保持する。
  const [showVideo, setShowVideo] = useState<boolean>(() => {
    return typeof persisted.showVideo === "boolean"
      ? persisted.showVideo
      : DEFAULT_SHOW_VIDEO;
  });

  // 設定モーダルのpayloadに含まれるが、この画面ではUIとしては表示しない値。
  // 他画面での設定を上書きしないため、localStorageの値を保持しておく。
  const [disableChartDownsample, setDisableChartDownsample] = useState<boolean>(
    () => {
      return typeof persisted.disableChartDownsample === "boolean"
        ? persisted.disableChartDownsample
        : DEFAULT_DISABLE_CHART_DOWNSAMPLE;
    },
  );
  const [disableMapDownsample, setDisableMapDownsample] = useState<boolean>(
    () => {
      return typeof persisted.disableMapDownsample === "boolean"
        ? persisted.disableMapDownsample
        : DEFAULT_DISABLE_MAP_DOWNSAMPLE;
    },
  );

  const [mapTileLayer, setMapTileLayer] = useState<MapTileLayer>(() => {
    return persisted.mapTileLayer ?? DEFAULT_MAP_TILE_LAYER;
  });

  const [mapActualBitrateColoring, setMapActualBitrateColoring] =
    useState<MapActualBitrateColoring>(() => {
      return (
        persisted.mapActualBitrateColoring ??
        DEFAULT_MAP_ACTUAL_BITRATE_COLORING
      );
    });

  // Mapページでは常に間引かず取得する（no_cap=1）。
  const disableUiCap = true;

  const [heatmapCellMeters, setHeatmapCellMeters] = useState<number>(() => {
    return typeof persisted.heatmapCellMeters === "number" &&
      Number.isFinite(persisted.heatmapCellMeters)
      ? persisted.heatmapCellMeters
      : DEFAULT_HEATMAP_CELL_METERS;
  });

  const [heatmapBaseOpacity, setHeatmapBaseOpacity] = useState<number>(() => {
    return typeof persisted.heatmapBaseOpacity === "number" &&
      Number.isFinite(persisted.heatmapBaseOpacity)
      ? persisted.heatmapBaseOpacity
      : DEFAULT_HEATMAP_BASE_OPACITY;
  });

  const [heatmapRenderMode, setHeatmapRenderMode] = useState<"grid" | "smooth">(
    () => {
      return persisted.heatmapRenderMode ?? DEFAULT_HEATMAP_RENDER_MODE;
    },
  );

  // スコア色閾値はGrid/Smoothで共通値を使用する。
  const [heatmapScoreColorMid, setHeatmapScoreColorMid] = useState<number>(
    () => {
      const v =
        typeof persisted.heatmapGridScoreColorMid === "number" &&
        Number.isFinite(persisted.heatmapGridScoreColorMid)
          ? persisted.heatmapGridScoreColorMid
          : typeof persisted.heatmapSmoothScoreColorMid === "number" &&
              Number.isFinite(persisted.heatmapSmoothScoreColorMid)
            ? persisted.heatmapSmoothScoreColorMid
            : DEFAULT_HEATMAP_SCORE_COLOR_MID;
      return v;
    },
  );

  const [heatmapScoreColorLow, setHeatmapScoreColorLow] = useState<number>(
    () => {
      const v =
        typeof persisted.heatmapGridScoreColorLow === "number" &&
        Number.isFinite(persisted.heatmapGridScoreColorLow)
          ? persisted.heatmapGridScoreColorLow
          : typeof persisted.heatmapSmoothScoreColorLow === "number" &&
              Number.isFinite(persisted.heatmapSmoothScoreColorLow)
            ? persisted.heatmapSmoothScoreColorLow
            : DEFAULT_HEATMAP_SCORE_COLOR_LOW;
      return v;
    },
  );

  const activeHeatmapScoreColorMid = heatmapScoreColorMid;
  const activeHeatmapScoreColorLow = heatmapScoreColorLow;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedInfo, setSelectedInfo] = useState<BpsLogMapPickInfo | null>(
    null,
  );

  const [selectedPointsSort, setSelectedPointsSort] = useState<
    "time_desc" | "time_asc" | "mbps_desc" | "mbps_asc"
  >("time_desc");

  const handlePickInfoChange = useCallback((info: BpsLogMapPickInfo | null) => {
    setSelectedInfo(info);
    if (info) setIsSidebarOpen(true);
  }, []);

  const sortedSelectedPoints = useMemo(() => {
    const pts = selectedInfo?.points ?? [];
    if (!pts.length) return pts;

    // timestampはJSTの文字列だが、Dateとして解釈できる前提。
    const toTimeDescSafe = (ts: string) => {
      const t = new Date(ts).getTime();
      return Number.isFinite(t) ? t : -Infinity;
    };
    const toTimeAscSafe = (ts: string) => {
      const t = new Date(ts).getTime();
      return Number.isFinite(t) ? t : Infinity;
    };
    const toMbpsAscSafe = (v: unknown) =>
      typeof v === "number" && Number.isFinite(v) ? v : Infinity;
    const toMbpsDescSafe = (v: unknown) =>
      typeof v === "number" && Number.isFinite(v) ? v : -Infinity;

    if (selectedPointsSort === "time_desc") {
      return [...pts].sort(
        (a, b) => toTimeDescSafe(b.timestamp) - toTimeDescSafe(a.timestamp),
      );
    }

    if (selectedPointsSort === "time_asc") {
      return [...pts].sort(
        (a, b) => toTimeAscSafe(a.timestamp) - toTimeAscSafe(b.timestamp),
      );
    }

    if (selectedPointsSort === "mbps_desc") {
      return [...pts].sort((a, b) => {
        const aa = toMbpsDescSafe(a.actual_bitrate);
        const bb = toMbpsDescSafe(b.actual_bitrate);
        if (aa !== bb) return bb - aa;
        return toTimeDescSafe(b.timestamp) - toTimeDescSafe(a.timestamp);
      });
    }

    // Mbps（低い順）: 弱い点を先に見たい用途。
    return [...pts].sort((a, b) => {
      const aa = toMbpsAscSafe(a.actual_bitrate);
      const bb = toMbpsAscSafe(b.actual_bitrate);
      if (aa !== bb) return aa - bb;
      return toTimeDescSafe(b.timestamp) - toTimeDescSafe(a.timestamp);
    });
  }, [selectedInfo?.points, selectedPointsSort]);

  const scoreLegendGradient = useMemo(() => {
    const midPct = Math.max(0, Math.min(100, activeHeatmapScoreColorMid * 100));
    const lowPct = Math.max(
      midPct,
      Math.min(100, activeHeatmapScoreColorLow * 100),
    );
    return `linear-gradient(90deg, ${mapActualBitrateColoring.highColor} 0%, ${mapActualBitrateColoring.midColor} ${midPct.toFixed(2)}%, ${mapActualBitrateColoring.lowColor} ${lowPct.toFixed(2)}%, ${mapActualBitrateColoring.lowColor} 100%)`;
  }, [
    activeHeatmapScoreColorMid,
    activeHeatmapScoreColorLow,
    mapActualBitrateColoring.lowColor,
    mapActualBitrateColoring.midColor,
    mapActualBitrateColoring.highColor,
  ]);

  const canApplyPeriodRange = useMemo(() => {
    const startAt = parseDateTimeLocal(startInput);
    const endAt = parseDateTimeLocal(endInput);
    if (!startAt || !endAt) return false;
    return endAt.getTime() >= startAt.getTime();
  }, [startInput, endInput]);

  const applyPeriodRange = () => {
    if (!canApplyPeriodRange) return;

    const startAt = parseDateTimeLocal(startInput);
    const endAt = parseDateTimeLocal(endInput);
    if (!startAt || !endAt) return;
    if (endAt.getTime() < startAt.getTime()) {
      window.alert("終了時刻を開始時刻以降に設定してください。");
      return;
    }

    // 取得ステータスの表示を即座に更新する（hook側のloading伝播待ちで一瞬「適用」に戻るのを防ぐ）。
    setIsPeriodRenderBusy(true);

    const isSameAsApplied =
      startInput === appliedStartInput && endInput === appliedEndInput;
    if (isSameAsApplied) {
      setAppliedFetchNonce((v) => v + 1);
      return;
    }

    setAppliedStartInput(startInput);
    setAppliedEndInput(endInput);
  };

  const {
    bpslogData,
    isRangeLoading: isBpslogRangeLoading,
    cancelRequests: cancelBpsLogRequests,
  } = useBpsLogData({
    vehicleId,
    isRealtime,
    appliedStartInput,
    appliedEndInput,
    appliedFetchNonce,
    realtimeWindowMinutes,
    delayOffsetMs,
    realtimeRightEdgeFallbackLagMs,
    maxPointsForUi,
    disableUiCap,
    initialStartInput,
    initialEndInput,
    setStartInput,
    setEndInput,
    setAppliedStartInput,
    setAppliedEndInput,
  });

  const isBpslogRangeLoadingRef = useRef(false);
  useEffect(() => {
    isBpslogRangeLoadingRef.current = isBpslogRangeLoading;
  }, [isBpslogRangeLoading]);

  useEffect(() => {
    if (isBpslogRangeLoading) {
      setIsPeriodRenderBusy(true);
      return;
    }
  }, [isBpslogRangeLoading]);

  const isApplyingPeriodRange = isBpslogRangeLoading || isPeriodRenderBusy;

  const handleCancelApply = () => {
    cancelBpsLogRequests();
    setRenderCancelToken((v) => v + 1);
    setIsPeriodRenderBusy(false);
  };

  const handleMapRenderBusyChange = useCallback((busy: boolean) => {
    if (busy) {
      setIsPeriodRenderBusy(true);
      return;
    }
    if (isBpslogRangeLoadingRef.current) return;
    setIsPeriodRenderBusy(false);
  }, []);

  const fitOnceRef = useRef(false);
  useEffect(() => {
    // Map側で setView を持っているため、最初のデータ取得時だけ追従ONにして
    // 座標がある点へ寄せる（以降はユーザー操作を尊重する）
    if (fitOnceRef.current) return;
    if (!bpslogData.length) return;
    const hasGps = bpslogData.some(
      (p) => p?.gps?.lat != null && p?.gps?.lng != null,
    );
    if (!hasGps) return;
    fitOnceRef.current = true;
    setFollowMap(true);
    window.setTimeout(() => setFollowMap(false), 0);
  }, [bpslogData]);

  return (
    <div className="bpslogScreen">
      <header className="bpslogHeader">
        <div className="bpslogHeaderLeft">
          <div className="bpslogLogo">
            <img src={logo} alt="" />
          </div>
          <label className="bpslogField bpslogField--vehicle">
            <select
              value={vehicleId ?? ""}
              onChange={(e) => setVehicleId(Number(e.target.value))}
              disabled={!vehicleOptions.length}
            >
              {!vehicleOptions.length ? (
                <option value="" disabled>
                  車両なし
                </option>
              ) : null}
              {vehicleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="bpslogHeaderCenter">
          <div className="bpslogTimeGroup" aria-label="期間">
            <label className="bpslogField bpslogField--start">
              <span>開始</span>
              <input
                type="datetime-local"
                value={startInput}
                onChange={(e) => setStartInput(e.target.value)}
                step={1}
              />
            </label>

            <label className="bpslogField bpslogField--end">
              <span>終了</span>
              <input
                type="datetime-local"
                value={endInput}
                onChange={(e) => setEndInput(e.target.value)}
                step={1}
              />
            </label>

            <button
              type="button"
              className="bpslogApplyButton"
              onClick={applyPeriodRange}
              disabled={!canApplyPeriodRange || isApplyingPeriodRange}
            >
              {isApplyingPeriodRange ? "取得中" : "適用"}
            </button>

            <button
              type="button"
              className="bpslogCancelButton"
              onClick={handleCancelApply}
              disabled={!isApplyingPeriodRange}
            >
              キャンセル
            </button>
          </div>
        </div>

        <div className="bpslogHeaderRight" aria-label="ヘッダー右側">
          <button
            type="button"
            className="bpslogSettingsButton"
            aria-label="設定"
            onClick={() => setIsSettingsModalOpen(true)}
          >
            <img
              src={settingsIcon}
              className="bpslogSettingsIcon"
              alt=""
              aria-hidden="true"
            />
            <span className="bpslogSrOnly">設定</span>
          </button>
          <button
            type="button"
            className="bpslogMapButton"
            aria-label="ログ画面へ"
            onClick={() =>
              navigate("/bpslog", {
                state: {
                  vehicleId,
                  startInput: appliedStartInput,
                  endInput: appliedEndInput,
                },
              })
            }
          >
            <img
              src={logpageIcon}
              className="bpslogLogPageIcon"
              alt=""
              aria-hidden="true"
            />
            <span className="bpslogSrOnly">ログ画面へ</span>
          </button>
        </div>
      </header>

      <SettingsModal
        isOpen={isSettingsModalOpen}
        initialDelayOffsetMs={delayOffsetMs}
        initialCharts={[]}
        initialFollowMap={followMap}
        initialShowVideo={showVideo}
        showDownsampleSettings={false}
        initialDisableChartDownsample={disableChartDownsample}
        initialDisableMapDownsample={disableMapDownsample}
        initialMapTileLayer={mapTileLayer}
        initialMapActualBitrateColoring={mapActualBitrateColoring}
        showHeatmapCellMeters={true}
        initialHeatmapCellMeters={heatmapCellMeters}
        initialHeatmapBaseOpacity={heatmapBaseOpacity}
        initialHeatmapRenderMode={heatmapRenderMode}
        initialHeatmapGridScoreColorMid={heatmapScoreColorMid}
        initialHeatmapGridScoreColorLow={heatmapScoreColorLow}
        initialHeatmapSmoothScoreColorMid={heatmapScoreColorMid}
        initialHeatmapSmoothScoreColorLow={heatmapScoreColorLow}
        defaultDelayOffsetMs={DEFAULT_DELAY_OFFSET_MS}
        defaultCharts={[]}
        defaultFollowMap={DEFAULT_FOLLOW_MAP}
        defaultShowVideo={DEFAULT_SHOW_VIDEO}
        defaultDisableChartDownsample={DEFAULT_DISABLE_CHART_DOWNSAMPLE}
        defaultDisableMapDownsample={DEFAULT_DISABLE_MAP_DOWNSAMPLE}
        defaultMapTileLayer={DEFAULT_MAP_TILE_LAYER}
        defaultMapActualBitrateColoring={DEFAULT_MAP_ACTUAL_BITRATE_COLORING}
        defaultHeatmapCellMeters={DEFAULT_HEATMAP_CELL_METERS}
        defaultHeatmapBaseOpacity={DEFAULT_HEATMAP_BASE_OPACITY}
        defaultHeatmapRenderMode={DEFAULT_HEATMAP_RENDER_MODE}
        defaultHeatmapGridScoreColorMid={DEFAULT_HEATMAP_SCORE_COLOR_MID}
        defaultHeatmapGridScoreColorLow={DEFAULT_HEATMAP_SCORE_COLOR_LOW}
        defaultHeatmapSmoothScoreColorMid={DEFAULT_HEATMAP_SCORE_COLOR_MID}
        defaultHeatmapSmoothScoreColorLow={DEFAULT_HEATMAP_SCORE_COLOR_LOW}
        onApply={(next) => {
          setDelayOffsetMs(next.delayOffsetMs);
          setFollowMap(next.followMap);
          setShowVideo(next.showVideo);
          if (typeof next.disableChartDownsample === "boolean") {
            setDisableChartDownsample(next.disableChartDownsample);
          }
          if (typeof next.disableMapDownsample === "boolean") {
            setDisableMapDownsample(next.disableMapDownsample);
          }
          setMapTileLayer(next.mapTileLayer);
          setMapActualBitrateColoring(next.mapActualBitrateColoring);

          const heatmapCellMetersToPersist =
            typeof next.heatmapCellMeters === "number" &&
            Number.isFinite(next.heatmapCellMeters)
              ? next.heatmapCellMeters
              : heatmapCellMeters;
          setHeatmapCellMeters(heatmapCellMetersToPersist);

          const heatmapBaseOpacityToPersist =
            typeof next.heatmapBaseOpacity === "number" &&
            Number.isFinite(next.heatmapBaseOpacity)
              ? Math.max(0, Math.min(1, next.heatmapBaseOpacity))
              : heatmapBaseOpacity;
          setHeatmapBaseOpacity(heatmapBaseOpacityToPersist);

          const heatmapRenderModeToPersist =
            next.heatmapRenderMode === "grid" ||
            next.heatmapRenderMode === "smooth"
              ? next.heatmapRenderMode
              : heatmapRenderMode;
          setHeatmapRenderMode(heatmapRenderModeToPersist);

          const nextMidRaw =
            typeof next.heatmapGridScoreColorMid === "number" &&
            Number.isFinite(next.heatmapGridScoreColorMid)
              ? next.heatmapGridScoreColorMid
              : typeof next.heatmapSmoothScoreColorMid === "number" &&
                  Number.isFinite(next.heatmapSmoothScoreColorMid)
                ? next.heatmapSmoothScoreColorMid
                : heatmapScoreColorMid;

          const safeMid = Math.max(
            0.01,
            Math.min(0.99, Math.max(0, Math.min(1, nextMidRaw))),
          );

          const nextLowRaw =
            typeof next.heatmapGridScoreColorLow === "number" &&
            Number.isFinite(next.heatmapGridScoreColorLow)
              ? next.heatmapGridScoreColorLow
              : typeof next.heatmapSmoothScoreColorLow === "number" &&
                  Number.isFinite(next.heatmapSmoothScoreColorLow)
                ? next.heatmapSmoothScoreColorLow
                : heatmapScoreColorLow;

          const safeLow = Math.max(
            safeMid,
            Math.max(0.01, Math.min(1, Math.max(0, Math.min(1, nextLowRaw)))),
          );

          setHeatmapScoreColorMid(safeMid);
          setHeatmapScoreColorLow(safeLow);

          // 設定をlocalStorageへ保存（チャート設定はこの画面では扱わないため上書きしない）。
          const toPersist: PersistedSettings = {
            delayOffsetMs: next.delayOffsetMs,
            charts: [],
            followMap: next.followMap,
            showVideo: next.showVideo,
            disableUiCap: next.disableUiCap,
            disableChartDownsample:
              typeof next.disableChartDownsample === "boolean"
                ? next.disableChartDownsample
                : disableChartDownsample,
            disableMapDownsample:
              typeof next.disableMapDownsample === "boolean"
                ? next.disableMapDownsample
                : disableMapDownsample,
            mapTileLayer: next.mapTileLayer,
            mapActualBitrateColoring: next.mapActualBitrateColoring,
            heatmapCellMeters: heatmapCellMetersToPersist,
            heatmapBaseOpacity: heatmapBaseOpacityToPersist,
            heatmapRenderMode: heatmapRenderModeToPersist,
            heatmapGridScoreColorMid: safeMid,
            heatmapGridScoreColorLow: safeLow,
            heatmapSmoothScoreColorMid: safeMid,
            heatmapSmoothScoreColorLow: safeLow,
          };

          persistSettingsMerged({
            storageKey: BPSLOG_SETTINGS_STORAGE_KEY,
            next: toPersist,
            transformMerged: (merged, prevAny, nextAny) => {
              const prev = prevAny as any;
              const nextObj = nextAny as any;
              if (
                Array.isArray(prev?.charts) &&
                Array.isArray(nextObj?.charts)
              ) {
                if (nextObj.charts.length === 0) {
                  return { ...merged, charts: prev.charts };
                }
              }
              return merged;
            },
          });
        }}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      <div className="bpslogGrid bpslogGrid--mapOnly">
        <div className="bpslogSurveyLayout" aria-label="電波調査">
          <aside
            className={`bpslogSurveySidebar ${isSidebarOpen ? "" : "isCollapsed"}`}
            aria-label="サイドメニュー"
          >
            <div className="bpslogSurveySidebarHeader">
              <button
                type="button"
                className="bpslogSurveyToggle"
                aria-label={
                  isSidebarOpen
                    ? "サイドメニューを折りたたむ"
                    : "サイドメニューを開く"
                }
                onClick={() => setIsSidebarOpen((v) => !v)}
              >
                {isSidebarOpen ? "≪" : "≫"}
              </button>
              {isSidebarOpen ? (
                <div className="bpslogSurveySidebarTitle">電波調査</div>
              ) : null}
            </div>

            {isSidebarOpen ? (
              <div className="bpslogSurveySidebarBody">
                <div className="bpslogSurveySection">
                  <div className="bpslogSurveySectionTitle">選択範囲情報</div>
                  {selectedInfo?.pickedPoint ? (
                    <div className="bpslogSurveyHoverInfo">
                      <div className="bpslogSurveyRow">
                        <span>時刻</span>
                        <span>
                          {formatTimeLabel(
                            selectedInfo.pickedPoint.timestamp,
                            true,
                          )}
                        </span>
                      </div>
                      <div className="bpslogSurveyRow">
                        <span>平均</span>
                        <span>
                          {typeof selectedInfo.stats.avgActualMbps ===
                            "number" &&
                          Number.isFinite(selectedInfo.stats.avgActualMbps)
                            ? `${selectedInfo.stats.avgActualMbps.toFixed(2)} Mbps`
                            : "--"}
                        </span>
                      </div>
                      <div className="bpslogSurveyRow">
                        <span>重なり</span>
                        <span>{selectedInfo.count} 件</span>
                      </div>
                      <div className="bpslogSurveyRow">
                        <span>スコア</span>
                        <span>
                          {Number.isFinite(selectedInfo.stats.weaknessScore)
                            ? selectedInfo.stats.weaknessScore.toFixed(3)
                            : "--"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bpslogSurveyHint">
                      {heatmapRenderMode === "smooth"
                        ? "地図をクリックして周辺の値を確認できます。"
                        : "地図上の色付きセルをクリックしてください。"}
                    </div>
                  )}
                </div>

                <div className="bpslogSurveySection">
                  <div className="bpslogSurveySectionTitleRow">
                    <div className="bpslogSurveySectionTitle">
                      選択範囲内データ
                    </div>
                    <label className="bpslogSurveySort">
                      <span>ソート</span>
                      <select
                        value={selectedPointsSort}
                        onChange={(e) =>
                          setSelectedPointsSort(
                            e.target.value as
                              | "time_desc"
                              | "time_asc"
                              | "mbps_desc"
                              | "mbps_asc",
                          )
                        }
                        aria-label="Selected points sort"
                      >
                        <option value="time_desc">時刻（新しい順）</option>
                        <option value="time_asc">時刻（古い順）</option>
                        <option value="mbps_desc">Mbps（高い順）</option>
                        <option value="mbps_asc">Mbps（低い順）</option>
                      </select>
                    </label>
                  </div>
                  {selectedInfo?.points?.length ? (
                    <div className="bpslogSurveyList" role="list">
                      {sortedSelectedPoints.slice(0, 50).map((p, idx) => {
                        const actual =
                          typeof p.actual_bitrate === "number" &&
                          Number.isFinite(p.actual_bitrate)
                            ? `${p.actual_bitrate.toFixed(2)} Mbps`
                            : "--";
                        return (
                          <div
                            className="bpslogSurveyListItem"
                            role="listitem"
                            key={idx}
                          >
                            <div className="bpslogSurveyListTime">
                              {formatTimeLabel(p.timestamp, true)}
                            </div>
                            <div className="bpslogSurveyListVal">{actual}</div>
                          </div>
                        );
                      })}
                      {sortedSelectedPoints.length > 50 ? (
                        <div className="bpslogSurveyHint">
                          表示は先頭50件のみ（全{sortedSelectedPoints.length}
                          件）
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="bpslogSurveyHint">--</div>
                  )}
                </div>

                <div className="bpslogSurveySection">
                  <div className="bpslogSurveySectionTitle">設定値</div>
                  <div className="bpslogSurveyHoverInfo">
                    <div className="bpslogSurveyLegend" aria-label="凡例">
                      <div className="bpslogSurveyLegendLabels">
                        <div className="bpslogSurveyLegendLabel">スコア</div>
                      </div>
                      <div
                        className="bpslogSurveyLegendBar bpslogSurveyLegendBar--relative"
                        style={{
                          background: scoreLegendGradient,
                        }}
                      >
                        <div
                          className="bpslogSurveyLegendCap bpslogSurveyLegendCap--left"
                          style={{
                            background: mapActualBitrateColoring.highColor,
                          }}
                        />
                        <div
                          className="bpslogSurveyLegendCap bpslogSurveyLegendCap--right"
                          style={{
                            background: mapActualBitrateColoring.lowColor,
                          }}
                        />
                      </div>
                      <div className="bpslogSurveyLegendLabels">
                        <div className="bpslogSurveyLegendLabel">0.00</div>
                        <div className="bpslogSurveyLegendLabel">
                          {activeHeatmapScoreColorMid.toFixed(2)}
                        </div>
                        <div className="bpslogSurveyLegendLabel">
                          {activeHeatmapScoreColorLow.toFixed(2)}
                        </div>
                        <div className="bpslogSurveyLegendLabel">1.00</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </aside>

          <div className="bpslogSurveyMain" aria-label="地図">
            <div className="bpslogCell bpslogCell--mapFrame" aria-label="地図">
              <BpsLogMap
                basePoint={basePoint}
                baseName={baseName}
                coursePoints={coursePoints}
                bpslogData={bpslogData}
                pointerPoint={null}
                followPointer={followMap}
                mapTileLayer={mapTileLayer}
                mapActualBitrateColoring={mapActualBitrateColoring}
                vehicleId={vehicleId}
                isRealtime={false}
                startInput={appliedStartInput}
                endInput={appliedEndInput}
                realtimeWindowMinutes={realtimeWindowMinutes}
                visualizationMode="heatmap"
                heatmapCellMeters={heatmapCellMeters}
                heatmapBaseOpacity={heatmapBaseOpacity}
                heatmapRenderMode={heatmapRenderMode}
                heatmapGridScoreColorMid={heatmapScoreColorMid}
                heatmapGridScoreColorLow={heatmapScoreColorLow}
                heatmapSmoothScoreColorMid={heatmapScoreColorMid}
                heatmapSmoothScoreColorLow={heatmapScoreColorLow}
                onPickInfoChange={handlePickInfoChange}
                onRenderBusyChange={handleMapRenderBusyChange}
                renderCancelToken={renderCancelToken}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BpsLogMapPage;
